import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { CollapsibleFormSection } from '../../../components/ui/CollapsibleFormSection';
import { InlineAlert } from '../../../components/ui/InlineAlert';
import {
  CompetencyAudience,
  EvaluationCycle,
  EvaluationCycleIndicator,
  PerformanceIndicator,
  listCycleIndicators,
  listPerformanceIndicators,
  replaceCycleIndicators,
} from '../../../services/peoplePerformanceApi';

const AUDIENCE_ORDER: CompetencyAudience[] = ['Colaborador', 'Gestor', 'Executivo'];

function audienceLabel(value?: string | null): string {
  if (value === 'Colaborador' || value === 'Gestor' || value === 'Executivo') {
    return value;
  }
  return value?.trim() || '—';
}

type EvaluationCycleIndicatorsSectionProps = {
  cycleId: string;
  cycle: EvaluationCycle | null;
  cycleLoading?: boolean;
  canWrite: boolean;
};

export function EvaluationCycleIndicatorsSection({
  cycleId,
  cycle,
  cycleLoading = false,
  canWrite,
}: EvaluationCycleIndicatorsSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const [allIndicators, setAllIndicators] = useState<PerformanceIndicator[]>([]);
  const [selectedIndicatorIds, setSelectedIndicatorIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const canEdit = canWrite && cycle?.status === 'Draft';

  const indicatorsByAudience = useMemo(() => {
    const groups = new Map<string, PerformanceIndicator[]>();
    for (const indicator of allIndicators) {
      const key = indicator.audience || 'Outros';
      const list = groups.get(key) ?? [];
      list.push(indicator);
      groups.set(key, list);
    }

    return AUDIENCE_ORDER.map((audience) => ({
      audience,
      indicators: groups.get(audience) ?? [],
    }));
  }, [allIndicators]);

  async function loadIndicators(): Promise<void> {
    setLoading(true);
    try {
      const [catalog, cycleIndicators] = await Promise.all([
        listPerformanceIndicators({ pageSize: 100, activeOnly: true }),
        listCycleIndicators(cycleId),
      ]);
      setAllIndicators(catalog.items);
      setSelectedIndicatorIds(cycleIndicators.map((item: EvaluationCycleIndicator) => item.indicatorId));
    } catch {
      toast.error('Falha ao carregar indicadores do ciclo.');
      setAllIndicators([]);
      setSelectedIndicatorIds([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (cycleLoading) {
      setLoading(true);
      return;
    }
    if (!canEdit) {
      setLoading(false);
      setAllIndicators([]);
      setSelectedIndicatorIds([]);
      return;
    }
    void loadIndicators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleId, canEdit, cycleLoading]);

  function toggleIndicator(indicatorId: string): void {
    setSelectedIndicatorIds((current) =>
      current.includes(indicatorId)
        ? current.filter((id) => id !== indicatorId)
        : [...current, indicatorId],
    );
  }

  async function handleSave(): Promise<void> {
    if (!canEdit) return;
    setSaving(true);
    try {
      const result = await replaceCycleIndicators(cycleId, selectedIndicatorIds);
      setSelectedIndicatorIds(result.map((item) => item.indicatorId));
      toast.success('Indicadores do ciclo salvos.');
    } catch {
      toast.error('Falha ao salvar indicadores do ciclo.');
    } finally {
      setSaving(false);
    }
  }

  if (cycleLoading && canWrite) {
    return (
      <section className="mb-4 rounded-xl border border-secondary-200 bg-white p-4">
        <h2 className="text-lg font-semibold text-secondary-900">Indicadores do ciclo</h2>
        <p className="mt-2 text-sm text-secondary-500">Carregando indicadores...</p>
      </section>
    );
  }

  if (!canEdit) {
    return null;
  }

  return (
    <CollapsibleFormSection
      title="Indicadores do ciclo"
      expanded={expanded}
      onToggle={() => setExpanded((current) => !current)}
      createSummary={
        loading
          ? 'Carregando indicadores...'
          : selectedIndicatorIds.length === 0
            ? 'Clique para expandir e selecionar os indicadores deste ciclo'
            : `${selectedIndicatorIds.length} indicador${selectedIndicatorIds.length === 1 ? '' : 'es'} selecionado${selectedIndicatorIds.length === 1 ? '' : 's'}`
      }
      className="mb-4"
    >
      <div className="px-4 pb-4 pt-3">
        <p className="mb-4 text-sm text-secondary-600">
          As competências vinculadas aos indicadores selecionados serão usadas nas avaliações deste ciclo.
        </p>
        {loading ? (
          <p className="text-sm text-secondary-500">Carregando indicadores...</p>
        ) : allIndicators.length === 0 ? (
          <InlineAlert>
            Nenhum indicador ativo cadastrado. Cadastre indicadores antes de vinculá-los ao ciclo.
          </InlineAlert>
        ) : (
          <div className="grid gap-4 md:grid-cols-3">
            {indicatorsByAudience.map((group) => (
              <div key={group.audience}>
                <h3 className="mb-2 text-sm font-semibold text-secondary-800">{audienceLabel(group.audience)}</h3>
                {group.indicators.length === 0 ? (
                  <p className="text-sm text-secondary-500">Nenhum indicador.</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {group.indicators.map((indicator) => (
                      <label key={indicator.id} className="erp-label-inline">
                        <input
                          type="checkbox"
                          checked={selectedIndicatorIds.includes(indicator.id)}
                          onChange={() => toggleIndicator(indicator.id)}
                        />
                        <span>{indicator.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="erp-btn-actions mt-4">
          <button
            type="button"
            className="erp-btn-primary"
            disabled={saving || loading}
            onClick={() => void handleSave()}
          >
            {saving ? 'Salvando...' : 'Salvar indicadores'}
          </button>
        </div>
      </div>
    </CollapsibleFormSection>
  );
}
